<script lang="ts">
	import type { Blok, Quotes } from '#library/components.schema.build.ts'
	import Shape from '#components/shape.svelte'
	import Pagination from '#components/pagination.svelte'
	import { attrs } from '#library/attrs.ts'
	import emblaCarouselSvelte from 'embla-carousel-svelte'
	import type { EmblaCarouselType } from 'embla-carousel'
	import { MediaQuery } from 'svelte/reactivity'
	import Image from '#components/image.svelte'
	import Link from '#components/link.svelte'

	interface Props {
		blok: Blok<Quotes>
	}

	const { current } = $derived(new MediaQuery('min-width: 640px'))

	let { blok }: Props = $props()
	let vertical = $derived(blok.vertical && current)
	let active = $state(0)
	let embla_api: EmblaCarouselType | undefined = $state()

	function onInit(event: CustomEvent) {
		embla_api = event.detail
		if (!embla_api) return

		embla_api.on('select', () => {
			active = embla_api?.selectedScrollSnap() ?? 0
		})
	}
</script>

<section class="quotes {blok.items?.[active]?.theme || blok.theme}" {...attrs(blok)}>
	<div
		class="viewport"
		class:contain={vertical}
		class:vertical
		use:emblaCarouselSvelte={{
			options: { loop: true, axis: vertical ? 'y' : 'x' },
			plugins: []
		}}
		onemblaInit={onInit}
	>
		<div class="items" class:vertical>
			{#if blok?.items?.length}
				{#each blok.items as { offset, ...item }, i (item._uid)}
					<div class="item" class:vertical>
						<div class="content" class:contain={!vertical} class:vertical class:image={item.image?.filename}>
							<div class="inner {offset}">
								{#if item.suptitle}
									<div class="suptitle">
										{item.suptitle.replace('{{pagination}}', `${i + 1} of ${blok.items.length}`)}
									</div>
								{/if}
								<blockquote class="blockquote">
									{#if !blok.nomark}
										<svg class="mark" class:offset>
											<use xlink:href="/images/vector.svg#quote"></use>
										</svg>
									{/if}
									{#if item.title}
										<div class="title" class:super={item.super} class:offset>{item.title}</div>
									{/if}
								</blockquote>
								{#if item.subtitle && !item.meta}
									<div class="subtitle">- {item.subtitle}</div>
								{/if}
								{#if item.meta && item.avatar?.filename}
									<div class="author">
										<Image src={item.avatar.filename} alt={item.avatar.alt} focus={item.avatar.focus} />
										<div>
											<div class="subtitle">{item.subtitle}</div>
											<div class="meta">{item.meta}</div>
										</div>
									</div>
								{/if}
							</div>
							{#if item.image?.filename}
								<div class="image">
									<Shape
										name={item.shape as never}
										src={item.image.filename}
										alt={item.image.alt}
										focus={item.image.focus}
									/>
								</div>
							{/if}
							{#if item.link?.url}
								<div class="link">
									<Link href={item.link.url} text={item.link.text} target={item.link.target} />
								</div>
							{/if}
						</div>
					</div>
				{/each}
			{/if}
		</div>
		{#if blok?.items?.length}
			<div class="pagi" class:vertical class:contain={!vertical}>
				<Pagination
					onprev={embla_api?.scrollPrev}
					onnext={embla_api?.scrollNext}
					onpage={embla_api?.scrollTo}
					items={blok.items?.length}
					vertical={blok.vertical}
					{active}
				/>
			</div>
		{/if}
	</div>
</section>

<style lang="css">
	.quotes {
		background-color: var(--c-background);
		color: var(--c-foreground);
	}

	.viewport {
		overflow: clip;

		&.vertical {
			display: flex;
			gap: 2rem;

			@media (--from-mobile) {
				height: 30rem;
				/* TODO: remove this and get height of tallest item */
			}
		}
	}

	.items {
		display: flex;
		gap: 1rem;

		&.vertical {
			@media (--from-mobile) {
				flex-direction: column;
			}
		}
	}

	.item {
		display: flex;
		align-items: center;
		flex: 0 0 100%;
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));

		&.vertical {
			min-height: 30rem;

			@media (--from-mobile) {
				flex: 0 0 auto;
			}
		}
	}

	.content {
		&.image {
			@media (--from-mobile) {
				align-items: center;
				display: grid;
				grid-template-columns: minmax(0, 8fr) minmax(0, 4fr);
			}
		}
	}

	.inner {
		@media (--from-mobile) {
			&.left {
				margin-inline-start: auto;
				max-width: 55rem;
			}

			&.right {
				margin-inline-end: auto;
				max-width: 55rem;
			}
		}
	}

	.suptitle {
		font: 400 var(--f-h6);
		letter-spacing: var(--ls-h6);
		margin-block-end: 2.5rem;
		text-transform: uppercase;
	}

	.blockquote {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		margin: 0;

		@media (--from-mobile) {
			flex-direction: row;
			gap: 2rem;
		}
	}

	.mark {
		color: var(--c-foreground);
		flex: 0 0 auto;
		size: 1.5rem 1.5rem;

		@media (--from-mobile) {
			size: 3rem 3.5rem;
		}

		&.offset {
			size: 1.5rem 1.5rem;

			@media (--from-mobile) {
				size: 2.5rem 2.5rem;
			}
		}
	}

	.title {
		font: 400 var(--f-h3);
		letter-spacing: var(--ls-h3);

		&.super {
			font: 600 var(--f-display);
			letter-spacing: var(--ls-display);
		}

		&.offset {
			color: var(--c-midground);
			font: 400 var(--f-h4);
			letter-spacing: var(--ls-h4);
		}
	}

	.subtitle {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin: 1rem 0 0;

		@media (--from-mobile) {
			margin: 1rem auto auto 4.75rem;
		}
	}

	.meta {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}

	.pagi {
		@media (--from-mobile) {
			align-self: center;
			translate: 0 -100%;
		}

		&.vertical {
			translate: 0 0;
		}
	}

	.author {
		align-items: center;
		display: flex;
		gap: 0.75rem;
		margin-block-start: 2rem;

		@media (--from-mobile) {
			gap: 1.5rem;
			margin-block-start: 3.5rem;
		}

		:global {
			.image {
				size: 2.5rem;

				@media (--from-mobile) {
					size: 6rem;
				}
			}
		}

		.subtitle {
			font: 500 var(--f-h5);
			letter-spacing: var(--ls-h5);
			margin: 0;
		}

		.meta {
			font: 400 var(--f-body1);
			letter-spacing: var(--ls-body1);
		}
	}

	.link {
		margin-block: 2rem -2rem;

		@media (--from-mobile) {
			inset: auto 0 0 auto;
			margin-block: 0;
			position: absolute;
		}
	}
</style>
